import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RENTAL_CATEGORIES } from "@/lib/rentals";
import PhotoDrop from "@/components/PhotoDrop";
import AddressSearch from "@/components/AddressSearch";
import { Home, Loader2, X, ShieldCheck, MapPin } from "lucide-react";

export default function RentalCreate() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const editId = sp.get("edit");

  const [f, setF] = useState({
    title: "", category: "Real Estate", description: "",
    images: [], location: "", address: "", currency: "USD",
    street: "", number: "", postcode: "", city: "", country: "",
    hourly_rate: "", daily_rate: "", weekly_rate: "", monthly_rate: "",
    security_deposit: "", min_units: 1,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!editId) return;
    api.get(`/rentals/${editId}`).then(r => {
      const i = r.data || {};
      setF({
        title: i.title || "", category: i.category || "Real Estate", description: i.description || "",
        images: i.images || [], location: i.location || "", address: i.address || "",
        street: i.street || "", number: i.number || "", postcode: i.postcode || "",
        city: i.city || "", country: i.country || "",
        currency: i.currency || "USD",
        hourly_rate: i.hourly_rate ?? "", daily_rate: i.daily_rate ?? "",
        weekly_rate: i.weekly_rate ?? "", monthly_rate: i.monthly_rate ?? "",
        security_deposit: i.security_deposit ?? "", min_units: i.min_units || 1,
      });
    }).catch(e => setErr(formatErr(e)));
  }, [editId]);

  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const rmImg = (k) => set("images", f.images.filter((_, idx) => idx !== k));

  const showMap = ["Real Estate", "Spaces"].includes(f.category);
  const composedAddress = [
    `${f.street || ""} ${f.number || ""}`.trim(),
    f.postcode,
    f.city,
    f.country,
  ].filter(Boolean).join(", ");
  const mapQuery = (f.address || composedAddress || f.location || "").trim();

  const submit = async (e) => {
    e.preventDefault();
    const rates = ["hourly_rate", "daily_rate", "weekly_rate", "monthly_rate"];
    if (!rates.some(k => Number(f[k]) > 0)) { setErr("Set at least one rate."); return; }
    setBusy(true); setErr("");
    try {
      const composed = [
        `${f.street || ""} ${f.number || ""}`.trim(),
        f.postcode, f.city, f.country,
      ].filter(Boolean).join(", ");
      const payload = {
        ...f,
        address: (f.address && f.address.trim()) || composed,
        location: (f.location && f.location.trim())
          || [f.city, f.country].filter(Boolean).join(", "),
        hourly_rate: f.hourly_rate === "" ? null : Number(f.hourly_rate),
        daily_rate: f.daily_rate === "" ? null : Number(f.daily_rate),
        weekly_rate: f.weekly_rate === "" ? null : Number(f.weekly_rate),
        monthly_rate: f.monthly_rate === "" ? null : Number(f.monthly_rate),
        security_deposit: Number(f.security_deposit || 0),
        min_units: Math.max(1, Number(f.min_units || 1)),
      };
      if (editId) { await api.patch(`/rentals/${editId}`, payload); nav(`/rentals/${editId}`); }
      else { const { data } = await api.post("/rentals", payload); nav(`/rentals/${data.id}`); }
    } catch (e) { setErr(formatErr(e)); } finally { setBusy(false); }
  };

  if (!user) return <div className="max-w-md mx-auto p-10 text-center"><p>Please sign in.</p></div>;

  return (
    <div className="max-w-2xl mx-auto px-6 sm:px-8 py-10" data-testid="rental-create-page">
      <p className="overline mb-1">{editId ? "Edit rental" : "New rental"}</p>
      <h1 className="h-display text-4xl">Rent it out, your way.</h1>

      <form onSubmit={submit} className="card p-6 mt-7 space-y-5" data-testid="rental-form">
        <Field label="Title" required>
          <input className="input" required minLength={3} maxLength={120} value={f.title}
            onChange={(e)=>set("title", e.target.value)} placeholder="e.g. Beachside apartment in Costa da Caparica" data-testid="rental-title-input"/>
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Category" required>
            <select className="input" value={f.category} onChange={(e)=>set("category", e.target.value)} data-testid="rental-category-select">
              {RENTAL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="City / Country (display)">
            <input className="input" value={f.location} onChange={(e)=>set("location", e.target.value)} placeholder="Lisbon, Portugal" data-testid="rental-location-input"/>
          </Field>
        </div>

        <Field label={showMap ? "Search address — then drag the pin to fine-tune the location" : "Search address (optional)"}>
          <AddressSearch
            value={f.address}
            placeholder={showMap ? "Type to search for the exact address…" : "Optional — improves map accuracy"}
            testid="rental-address"
            showMap={showMap}
            onChange={({ address, street, number, postcode, city, country }) => {
              setF(s => ({
                ...s,
                address: address || "",
                street:   street   ?? s.street,
                number:   number   ?? s.number,
                postcode: postcode ?? s.postcode,
                city:     city     || s.city,
                country:  country  || s.country,
                location: (city || country)
                  ? [city, country].filter(Boolean).join(", ")
                  : s.location,
              }));
            }}
          />
        </Field>

        {/* Structured address parts — always visible so hosts can edit or fill manually */}
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-3" data-testid="rental-address-fields">
          <div className="sm:col-span-4">
            <Field label="Street">
              <input className="input" value={f.street} onChange={(e)=>set("street", e.target.value)} placeholder="Rua da Boavista" data-testid="rental-street-input"/>
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Number">
              <input className="input" value={f.number} onChange={(e)=>set("number", e.target.value)} placeholder="12B" data-testid="rental-number-input"/>
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Post code">
              <input className="input" value={f.postcode} onChange={(e)=>set("postcode", e.target.value)} placeholder="1200-070" data-testid="rental-postcode-input"/>
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="City">
              <input className="input" value={f.city} onChange={(e)=>set("city", e.target.value)} placeholder="Lisbon" data-testid="rental-city-input"/>
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Country">
              <input className="input" value={f.country} onChange={(e)=>set("country", e.target.value)} placeholder="Portugal" data-testid="rental-country-input"/>
            </Field>
          </div>
        </div>

        {showMap && mapQuery && !(f.street || f.city || f.country) && (
          <div className="rounded-2xl overflow-hidden border border-[#E5E5E3]" data-testid="rental-map-preview">
            <iframe
              title="Map preview"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=14&output=embed`}
              width="100%" height="220"
              style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
            />
            <p className="text-[11px] text-[#8A8A8A] px-3 py-2 inline-flex items-center gap-1">
              <MapPin size={11}/> Search an address above to get a draggable pin.
            </p>
          </div>
        )}

        <Field label="Description" required>
          <textarea required rows={5} minLength={10} maxLength={4000} className="input"
            value={f.description} onChange={(e)=>set("description", e.target.value)}
            placeholder="Describe the rental, the rules, what's included, neighborhood vibe."
            data-testid="rental-description-input"/>
        </Field>

        {/* Rates */}
        <div>
          <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Rates (set any that apply)</span>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            {[
              { id: "hourly_rate",  lbl: "/hour"  },
              { id: "daily_rate",   lbl: "/day"   },
              { id: "weekly_rate",  lbl: "/week"  },
              { id: "monthly_rate", lbl: "/month" },
            ].map(({ id, lbl }) => (
              <label key={id} className="block">
                <span className="text-xs text-[#8A8A8A]">{lbl}</span>
                <input type="number" min="0" step="1" className="input mt-1" value={f[id]}
                  onChange={(e)=>set(id, e.target.value)} data-testid={`rental-${id}`}/>
              </label>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Security deposit (refundable)">
            <input type="number" min="0" step="1" className="input" value={f.security_deposit}
              onChange={(e)=>set("security_deposit", e.target.value)} data-testid="rental-deposit"/>
          </Field>
          <Field label="Min units">
            <input type="number" min="1" className="input" value={f.min_units}
              onChange={(e)=>set("min_units", e.target.value)} data-testid="rental-min-units"/>
          </Field>
          <Field label="Currency">
            <input className="input" maxLength={6} value={f.currency}
              onChange={(e)=>set("currency", e.target.value.toUpperCase())} data-testid="rental-currency"/>
          </Field>
        </div>

        {/* Photos */}
        <div data-testid="rental-photos-section">
          <div className="flex items-baseline justify-between">
            <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Photos · {f.images.length}/8</span>
            <span className="text-[11px] text-[#8A8A8A]">First photo is the cover</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2" data-testid="rental-images">
            {f.images.map((src, k) => (
              <div key={k} className={`relative aspect-square rounded-xl overflow-hidden bg-[#F2F2F0] ${k===0 ? "ring-2 ring-[#0A0A0A]" : ""}`}>
                <img src={src} alt="" className="w-full h-full object-cover"/>
                {k === 0 && (
                  <span className="absolute top-1.5 left-1.5 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#0A0A0A] text-white">Cover</span>
                )}
                <button type="button" onClick={()=>rmImg(k)}
                  className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/60 text-white inline-flex items-center justify-center hover:bg-[#B84B4B]"
                  data-testid={`rental-image-rm-${k}`}><X size={12}/></button>
              </div>
            ))}
            {f.images.length < 8 && (
              <div className="aspect-square">
                <PhotoDrop
                  multiple
                  onPick={(url)=>setF(s => s.images.length >= 8 ? s : ({ ...s, images: [...s.images, url] }))}
                  aspect="square"
                  label={f.images.length === 0 ? "Add photos" : "Add more"}
                  testid="rental-photo-drop"
                  maxSize={1400}
                />
              </div>
            )}
          </div>
          <p className="text-[11px] text-[#8A8A8A] mt-2">Tip: drag-select multiple photos at once — they&apos;ll upload and show as tiles instantly.</p>
        </div>

        {err && <p className="text-sm text-[#B84B4B]" data-testid="rental-form-error">{err}</p>}
        <p className="text-xs text-[#8A8A8A] inline-flex items-center gap-1">
          <ShieldCheck size={12}/> All bookings go through Hoomanz Stripe checkout. We hold the deposit — you decide when to refund. 2 % Hoomanz Tip added on top.
        </p>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={()=>nav("/rentals")} className="btn-ghost flex-1">Cancel</button>
          <button className="btn-primary flex-1" disabled={busy} data-testid="rental-submit">
            {busy ? <><Loader2 size={14} className="animate-spin"/> Publishing…</> : <><Home size={14}/> {editId ? "Save changes" : "Publish rental"}</>}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">{label}{required && <span className="text-[#0057FF]"> *</span>}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
